import React, { useMemo } from "react";
import { Input, Button } from "antd";
import { PlusOutlined, MinusOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import DeleteIcon from "../../../assets/images/delete1.svg";

const MixSummary = ({
  customMixItems,
  groupName,
  setGroupName,
  groupQty,
  handleGroupQtyChange,
  handleQuantityChange,
  handleDelete,
  handleSubmit,
  submitting,
  precision,
  shouldOpenWeightPopup,
  openWeightPopupForGrouped,
  stockFailedItems = [],
}) => {
  const totals = useMemo(() => {
    let totalItems = 0;
    let totalQty = 0;
    let totalPrice = 0;
    customMixItems.forEach((item) => {
      const unitPrice = parseFloat(item.sunitprice) || 0;
      const qty = item.weight * (groupQty || 1);
      totalItems += 1;
      totalQty += qty;
      totalPrice += unitPrice * qty;
    });
    return { totalItems, totalQty: parseFloat(totalQty.toFixed(precision)), totalPrice: parseFloat(totalPrice.toFixed(precision)) };
  }, [customMixItems, groupQty, precision]);

  return (
    <>
      {/* Header — Group Name & Qty */}
      <div className="cm-summary-header">
        <h3>Mix Summary</h3>
        <div className="cm-group-controls">
          <div style={{ display: "flex", alignItems: "center" }}>
            <label>Name:</label>
            <Input
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="cm-group-name-input"
            />
          </div>
          <div className="cm-group-qty-controls">
            <label>Qty:</label>
            <Button
              icon={<MinusOutlined style={{ fontSize: 12 }} />}
              className="cm-qty-btn"
              disabled={customMixItems.length === 0 || groupQty <= 1}
              onClick={() => handleGroupQtyChange(Math.max(groupQty - 1, 1))}
            />
            <Input
              type="number"
              min={1}
              value={groupQty}
              onChange={(e) => {
                const newGroupQty = Math.max(Number(e.target.value) || 1, 1);
                handleGroupQtyChange(newGroupQty);
              }}
              onKeyDown={(e) => {
                if (e.key === "." || e.key === "e" || e.key === "-" || e.key === "+") e.preventDefault();
              }}
              onBlur={(e) => {
                const value = Number(e.target.value) || 1;
                handleGroupQtyChange(Math.max(value, 1));
              }}
              className="cm-group-qty-input"
              disabled={customMixItems.length === 0}
            />
            <Button
              icon={<PlusOutlined style={{ fontSize: 12, color: "#fff" }} />}
              className="cm-qty-btn"
              style={{ background: "#2F3856" }}
              disabled={customMixItems.length === 0}
              onClick={() => handleGroupQtyChange(groupQty + 1)}
            />
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="cm-table-header">
        <div style={{ width: "30%" }}>Product</div>
        <div style={{ width: "18%", textAlign: "right" }}>Price</div>
        <div style={{ width: "20%", textAlign: "center" }}>Qty</div>
        <div style={{ width: "12%", textAlign: "right" }}>Total Qty</div>
        <div style={{ width: "15%", textAlign: "right" }}>Total</div>
        <div style={{ width: "5%" }} />
      </div>

      {/* Items or Empty State */}
      {customMixItems.length === 0 ? (
        <div className="cm-empty-state">
          <ShoppingCartOutlined style={{ fontSize: 48, color: "#ddd" }} />
          <p>Add products from the left panel</p>
        </div>
      ) : (
        <div className="cm-table-body">
          {customMixItems.map((item) => {
            const unitPrice = parseFloat(item.sunitprice) || 0;
            const quantity = parseFloat((item.weight * (groupQty || 1)).toFixed(precision)) || 0;
            const totalPrice = parseFloat((unitPrice * quantity).toFixed(precision));
            const decimal = item?.uomData?.[0]?.decimal === "Y";
            const hasAltUom = item?.alternateUom?.length > 0 && item?.showAlternateUomInPos === "Y";
            const selectedUomName = item.selectedUom?.name || item?.uomData?.[0]?.name || "";
            const isStockFailed = stockFailedItems.includes(item.value || item.mProductId);

            return (
              <div key={item.lineId || item.name} className={`cm-table-row${isStockFailed ? " cm-stock-failed" : ""}`}>
                <div style={{ width: "30%" }}>
                  <p className="cm-item-name" title={item.name}>{item.name}</p>
                  {selectedUomName && (
                    <p className="cm-item-uom">
                      {selectedUomName}
                      {hasAltUom && <span style={{ color: "#1890ff", marginLeft: 4 }}>(Alt)</span>}
                    </p>
                  )}
                </div>
                <div style={{ width: "18%", textAlign: "right" }}>
                  <span className="cm-item-price">{unitPrice.toLocaleString("en-IN")}</span>
                </div>
                <div style={{ width: "20%", textAlign: "center" }}>
                  <Input
                    type="text"
                    value={item.rawWeight || item.weight}
                    readOnly={shouldOpenWeightPopup(item)}
                    onClick={() => {
                      if (shouldOpenWeightPopup(item)) {
                        openWeightPopupForGrouped(item, async (updatedProduct) => {
                          const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();
                          await handleQuantityChange(item, newValue);
                        });
                      }
                    }}
                    onChange={(e) => handleQuantityChange(item, e.target.value)}
                    onKeyDown={(e) => {
                      const currentValue = e.target.value;
                      if (currentValue === "0" && /\d/.test(e.key) && !e.target.value.startsWith("0.")) e.target.select();
                      if (decimal && e.key === "." && currentValue.includes(".")) { e.preventDefault(); return; }
                      if (!decimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) { e.preventDefault(); return; }
                      if (decimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) { e.preventDefault(); return; }
                    }}
                    onBlur={(e) => {
                      if (decimal && [".", "0.", "0.0", "0.00"].includes(e.target.value)) {
                        handleQuantityChange(item, "0");
                      }
                    }}
                    placeholder={decimal ? "0.001" : "1"}
                    className="cm-qty-input"
                  />
                </div>
                <div style={{ width: "12%", textAlign: "right" }}>
                  <span className="cm-item-price">{quantity}</span>
                </div>
                <div style={{ width: "15%", textAlign: "right" }}>
                  <span className="cm-item-price">{totalPrice.toLocaleString("en-IN")}</span>
                </div>
                <div style={{ width: "5%", textAlign: "center" }}>
                  <img
                    src={DeleteIcon}
                    className="cm-delete-btn"
                    onClick={() => handleDelete(item)}
                    alt="delete"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer — Totals + Submit */}
      <div className="cm-summary-footer">
        <div className="cm-totals-row">
          <span>Items</span>
          <span>{totals.totalItems}</span>
        </div>
        <div className="cm-totals-row">
          <span>Total Qty</span>
          <span>{parseFloat(totals.totalQty.toFixed(precision))}</span>
        </div>
        <div className="cm-totals-row cm-grand-total">
          <span>Total</span>
          <span>{totals.totalPrice.toLocaleString("en-IN")}</span>
        </div>
        <Button
          className="cm-submit-btn"
          disabled={submitting || customMixItems.length === 0}
          onClick={handleSubmit}
        >
          {submitting ? "Processing..." : "Add to Cart"}
        </Button>
      </div>
    </>
  );
};

export default MixSummary;
